import { BaseService } from './base-service.js';
import { tokenProvider } from './auth/token-provider.js';

class OrgService extends BaseService {

  getSessionInfo() {
    const sessionData = tokenProvider.getSessionData();
    if (!sessionData?.orgId || !sessionData?.userData?.id) {
      throw new Error('Missing required session data');
    }
    return { orgId: sessionData.orgId, userId: sessionData.userData.id };
  }

  async getCurrentOrg() {
    try {
      const { orgId } = this.getSessionInfo();
      const response = await this.get(`/org/${orgId}`);
      return response || null;
    } catch (error) {
      console.warn('Error fetching org data:', error);
      return null;
    }
  }

  async getOrgData(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    try {
      const response = await this.get(`/org/${orgId}`);
      return response || null;
    } catch (error) {
      console.warn('Error fetching org data:', error);
      return null;
    }
  }

  async updateOrg(orgId, updates) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    const response = await this.put(`/org/${orgId}`, updates);
    return response.data || null;
  }

  async getMembers(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    try {
      const response = await this.get(`/org/${orgId}/members`);
      return response || [];
    } catch (error) {
      console.warn('Error fetching members:', error);
      return [];
    }
  }
  async getLoggedInMember(orgId, userId) {
    if (!orgId || !userId) {
      throw new Error('Organization ID and user ID required');
    }
    return this.get(`/org/${orgId}/members/${userId}`);
  }
  
  async addMember(orgId, memberData) {
    if (!orgId || !memberData) {
      throw new Error('Organization ID and member data required');
    }
    try {
      const response = await this.post(`/org/${orgId}/members`, memberData);
      return response; // Return the response directly
    } catch (error) {
      console.error('Error in addMember:', error);
      throw error;
    }
  }

  async updateMember(orgId, memberId, updates) {
    if (!orgId || !memberId) {
      throw new Error('Organization ID and member ID required');
    }
    
    try {
      // Log the update request to verify metadata is included
      console.log('Updating member with data:', JSON.stringify(updates));
      
      // Ensure metadata is correctly structured
      if (updates.metadata) {
        // Make sure metadata is properly structured even if some properties are undefined
        const metadata = { 
          ...updates.metadata,
          title: updates.metadata.title || '',
          department: updates.metadata.department || ''
        };
        updates.metadata = metadata;
      }
      
      if (updates.permissions && !updates.permissions_json) {
        try {
          updates.permissions_json = JSON.stringify(updates.permissions);
        } catch (error) {
          console.warn('Failed to serialize permissions for updateMember request:', error);
        }
      }
      
      const response = await this.put(`/org/${orgId}/members/${memberId}`, updates);
      return response; // Return the response directly
    } catch (error) {
      console.error('Error in updateMember:', error);
      throw error;
    }
  }

  async removeMember(orgId, memberId) {
    if (!orgId || !memberId) {
      throw new Error('Organization ID and member ID required');
    }
    const response = await this.delete(`/org/${orgId}/members/${memberId}`);
    return response.data || null;
  }

  async rotateOrgToken(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    const response = await this.post(`/org/${orgId}/secrets/rotate-all`);
    return response.data || null;
  }

  // The following methods are kept for backward compatibility but may need adjustment
  // as they might not have direct equivalents in the new API
  
  async listCapabilities(orgId) {
    console.warn('listCapabilities may not be available in the new API');
    return [];
  }

  async createCapability(orgId, capabilityData) {
    console.warn('createCapability may not be available in the new API');
    return null;
  }

  async revokeCapability(orgId, capabilityId) {
    console.warn('revokeCapability may not be available in the new API');
    return null;
  }

  async getBillingInfo(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    try {
      const billingData = await this.get(`/org/${orgId}/billing`);
      return billingData || {
        plan: 'Free',
        credits: 0,
        usage: 0,
        invoices: [],
        plans: [
          {
            name: 'Free',
            credits: 100,
            price: 0
          },
          {
            name: 'Starter',
            credits: 1000,
            price: 49,
            priceId: 'price_starter'
          },
          {
            name: 'Professional',
            credits: 5000,
            price: 99,
            priceId: 'price_professional'
          },
          {
            name: 'Enterprise',
            credits: 'Unlimited',
            price: 299,
            priceId: 'price_enterprise'
          }
        ]
      };
    } catch (error) {
      console.warn('No billing info found:', error);
      return {
        plan: 'Free',
        credits: 0,
        usage: 0,
        invoices: [],
        plans: [
          {
            name: 'Free',
            credits: 100,
            price: 0
          },
          {
            name: 'Starter',
            credits: 1000,
            price: 49,
            priceId: 'price_starter'
          },
          {
            name: 'Professional',
            credits: 5000,
            price: 99,
            priceId: 'price_professional'
          },
          {
            name: 'Enterprise',
            credits: 'Unlimited',
            price: 299,
            priceId: 'price_enterprise'
          }
        ]
      };
    }
  }

  async updateBillingInfo(orgId, updates) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    return this.put(`/org/${orgId}/billing`, updates);
  }

  async listApiKeys(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    return this.get(`/org/${orgId}/api-keys`);
  }

  async createApiKey(orgId, keyData) {
    if (!orgId || !keyData) {
      throw new Error('Organization ID and key data required');
    }
    return this.post(`/org/${orgId}/api-keys`, keyData);
  }

  async updateApiKey(orgId, keyId, updates) {
    if (!orgId || !keyId) {
      throw new Error('Organization ID and key ID required');
    }
    return this.put(`/org/${orgId}/api-keys/${keyId}`, updates);
  }

  async revokeApiKey(orgId, keyId) {
    if (!orgId || !keyId) {
      throw new Error('Organization ID and key ID required');
    }
    return this.delete(`/org/${orgId}/api-keys/${keyId}`);
  }
  
  async deleteApiKey(orgId, keyId) {
    if (!orgId || !keyId) {
      throw new Error('Organization ID and key ID required');
    }
    return this.delete(`/org/${orgId}/api-keys/${keyId}`);
  }

  async verifyApiKey(orgId, verifyData) {
    if (!orgId || !verifyData) {
      throw new Error('Organization ID and verify data required');
    }
    return this.post(`/org/${orgId}/api-keys/verify`, verifyData);
  }

  async getCapabilityGroups(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    try {
      const groups = await this.get(`/org/${orgId}/capability-groups`);
      return groups || [];
    } catch (error) {
      console.warn('Error fetching capability groups:', error);
      return [];
    }
  }

  async createCapabilityGroup(orgId, groupData) {
    if (!orgId || !groupData) {
      throw new Error('Organization ID and group data required');
    }
    const response = await this.post(`/org/${orgId}/capability-groups`, groupData);
    return response.data || null;
  }

  async updateCapabilityGroup(orgId, groupId, updates) {
    if (!orgId || !groupId) {
      throw new Error('Organization ID and group ID required');
    }
    const response = await this.put(`/org/${orgId}/capability-groups/${groupId}`, updates);
    return response.data || null;
  }

  async deleteCapabilityGroup(orgId, groupId) {
    if (!orgId || !groupId) {
      throw new Error('Organization ID and group ID required');
    }
    const response = await this.delete(`/org/${orgId}/capability-groups/${groupId}`);
    return response.data || null;
  }


  async sendMagicLink(orgId, email, firstName = '', lastName = '', forceEmail = false) {
    if (!orgId || !email) {
      throw new Error('Organization ID and email required');
    }
    
    // If firstName and lastName are not provided, use a default or extract from email
    const defaultFirstName = firstName || email.split('@')[0];
    const defaultLastName = lastName || 'User';
    
    return this.post(`/org/${orgId}/magic-links`, { 
      email, 
      firstName: defaultFirstName,
      lastName: defaultLastName,
      forceEmail
    }, {
      headers: await this.getHeaders()
    });
  }

  async createPortalSession(orgId) {
    if (!orgId) {
      throw new Error('Organization ID required');
    }
    return this.post(`/org/${orgId}/billing/portal-session`);
  }
}

export const orgService = new OrgService(); 
